<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'PrinterTester',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    printerId: {
      type: String,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const reloadJobAction = (...args) => {
    const [job] = args;

    job.busy = true;

    return $fn.value
      .fetch('print_job', {
        parameters: { printer: props.printerId, id: job.id },
      })
      .then((data) => {
        console.log(data);
        _.extend(job, data);
        job.busy = false;
      });
  };

  const selectedFile = ref(null);
  const jobs = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    selectedFile: selectedFile.value,
    jobs: jobs.value,
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'selectedFile' }, { name: 'jobs' }, { name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { selectedFile, jobs, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.printerId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('printerId');
    }
  );

  const act_8564662037462133_showModal_fby620h8b_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'DataViewerModal',
      props: {
        mode: 'yaml',
        content: $event.data.response.data,
      },
    };
  };
  const act_8564662037462133_request_kcomgljq2_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'printer_attributes',
      data: {
        printer: props.printerId,
      },
    };
  };
  const act_4186377094414664_setData_ye5m1ghau_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'jobs',
      value: $event.data.response.data.jobs,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_4186377094414664_request_ead8k9up9_getActionArgs = async (
    $event
  ) => {
    return {
      method: 'POST',
      operation: 'print_document',
      data: {
        printer: props.printerId,
        document: selectedFile.value,
      },
      contentType: 'multipart/form-data',
    };
  };
  const act_4747808328624104_script_95jhbxnyv_value_callable = async (
    $event
  ) => {
    return reloadJobAction($event.scope.item);
  };
  const act_4747808328624104_script_95jhbxnyv_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_4747808328624104_script_95jhbxnyv_value_callable($event);
      },
    };
  };

  const act_7315092382398562_reloadComponentData_gmeo3hw01 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_8sgijptze =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_gmeo3hw01,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_8sgijptze],
    event: 'reload',
  });
  const act_8564662037462133_showModal_fby620h8b =
    new actionTypes.ShowModalAction({
      actionArgs: act_8564662037462133_showModal_fby620h8b_getActionArgs,
    });
  const inst_evh_8564662037462133_success_q6yzwk6jj =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_showModal_fby620h8b,
      event: 'success',
      displayName: 'showModal',
    });
  const act_8564662037462133_request_kcomgljq2 = new actionTypes.RequestAction({
    actionArgs: act_8564662037462133_request_kcomgljq2_getActionArgs,
    displayName: 'getAttributes',
    events: [inst_evh_8564662037462133_success_q6yzwk6jj],
  });
  const inst_evh_8564662037462133_clickPrevent_j3mdoevbo =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_request_kcomgljq2,
      event: 'click.prevent',
      displayName: 'getAttributes',
    });
  const inst_evh_8564662037462133_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8564662037462133_clickPrevent_j3mdoevbo],
      event: 'click.prevent',
    });
  const act_4186377094414664_setData_ye5m1ghau = new actionTypes.SetDataAction({
    actionArgs: act_4186377094414664_setData_ye5m1ghau_getActionArgs,
  });
  const inst_evh_4186377094414664_success_elnkggdkw =
    new actionTypes.EventHandlerImpl({
      action: act_4186377094414664_setData_ye5m1ghau,
      event: 'success',
      displayName: 'setData',
    });
  const act_4186377094414664_request_ead8k9up9 = new actionTypes.RequestAction({
    actionArgs: act_4186377094414664_request_ead8k9up9_getActionArgs,
    displayName: 'printFile',
    events: [inst_evh_4186377094414664_success_elnkggdkw],
  });
  const inst_evh_4186377094414664_click_gfpc8kq93 =
    new actionTypes.EventHandlerImpl({
      action: act_4186377094414664_request_ead8k9up9,
      event: 'click',
      displayName: 'printFile',
    });
  const inst_evh_4186377094414664_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4186377094414664_click_gfpc8kq93],
    event: 'click',
  });
  const act_4747808328624104_script_95jhbxnyv = new actionTypes.ScriptAction({
    actionArgs: act_4747808328624104_script_95jhbxnyv_getActionArgs,
  });
  const inst_evh_4747808328624104_click_m479fwc6m =
    new actionTypes.EventHandlerImpl({
      action: act_4747808328624104_script_95jhbxnyv,
      event: 'click',
      displayName: 'script',
    });
  const inst_evh_4747808328624104_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4747808328624104_click_m479fwc6m],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_reload_8sgijptze,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_success_q6yzwk6jj,
    inst_evh_8564662037462133_clickPrevent_j3mdoevbo,
    getAttributes: inst_evh_8564662037462133_clickPrevent_j3mdoevbo,
    inst_evh_8564662037462133_clickPrevent,
    inst_evh_4186377094414664_success_elnkggdkw,
    inst_evh_4186377094414664_click_gfpc8kq93,
    printFile: inst_evh_4186377094414664_click_gfpc8kq93,
    inst_evh_4186377094414664_click,
    inst_evh_4747808328624104_click_m479fwc6m,
    inst_evh_4747808328624104_click,
  };

  const evh_7315092382398562_reload_8sgijptze = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_8sgijptze.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_success_q6yzwk6jj = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_success_q6yzwk6jj.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_clickPrevent_j3mdoevbo = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_clickPrevent_j3mdoevbo.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4186377094414664_success_elnkggdkw = async (event, scope) => {
    return await actions.inst_evh_4186377094414664_success_elnkggdkw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4186377094414664_click_gfpc8kq93 = async (event, scope) => {
    return await actions.inst_evh_4186377094414664_click_gfpc8kq93.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4186377094414664_click = async (event, scope) => {
    return await actions.inst_evh_4186377094414664_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4747808328624104_click_m479fwc6m = async (event, scope) => {
    return await actions.inst_evh_4747808328624104_click_m479fwc6m.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4747808328624104_click = async (event, scope) => {
    return await actions.inst_evh_4747808328624104_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpFormGroup :label-cols-md="3" :content-cols-md="9">
      <ItpButton
        :text="pgettext('Button', 'Get printer attributes...')"
        :spinning="actions.getAttributes && actions.getAttributes.isRunning"
        @click.prevent="evh_8564662037462133_clickPrevent($event, {})"
      >
      </ItpButton>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="3"
      :content-cols-md="9"
      :label="pgettext('PrinterConfig', 'Print file')"
      :helpTooltip="
        pgettext(
          'PrinterConfig',
          'Select a file to send to the network printer for testing purposes.'
        )
      "
    >
      <BFormFile
        v-model="selectedFile"
        :placeholder="
          pgettext('PrinterConfig', 'Choose a file or drop it here')
        "
        :drop-placeholder="pgettext('PrinterConfig', 'Drop file here')"
        lang="hu"
        accept="pdf"
        class="col mb-3"
      >
      </BFormFile>
      <ItpButton
        :disabled="!selectedFile"
        :text="gettext('Print')"
        icon="fa-printer"
        :spinning="actions.printFileAction && actions.printFileAction.isRunning"
        class="col-auto"
        @click="evh_4186377094414664_click($event, {})"
      >
      </ItpButton>
    </ItpFormGroup>
    <table class="table table-condensed table-sm" v-if="jobs && jobs.length">
      <thead>
        <tr>
          <th></th>
          <th>
            {{ pgettext('PrinterTester|ColHead', 'ID') }}
          </th>
          <th>
            {{ pgettext('PrinterTester|ColHead', 'State') }}
          </th>
          <th>
            {{ pgettext('PrinterTester|ColHead', 'State reasons') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr :key="item.id" v-for="item in jobs">
          <td>
            <ItpButton
              :text="gettext('Reload')"
              icon="fa-refresh"
              :spinning="item.busy"
              @click="evh_4747808328624104_click($event, { item })"
            >
            </ItpButton>
          </td>
          <td>
            <strong>{{ item.id }}</strong>
          </td>
          <td>
            {{ item.state }}
          </td>
          <td>
            {{ item.state_reasons }}
          </td>
        </tr>
      </tbody>
    </table>
  </ItpUsercontrol>
</template>
